import React, { useState, useRef, useCallback, useEffect } from "react";
import { Player, type PlayerRef } from "@remotion/player";
import { useDynamicComponent } from "@/hooks/useDynamicComponent";
import { useLocalStorage } from "@/hooks/useLocalStorage";

/**
 * Creates a stable wrapper component that reads the latest dynamic
 * component from a ref. The Player mounts the wrapper once and never
 * remounts — swapping the ref triggers a re-render (via the parent)
 * which makes the wrapper render the new inner component, all without
 * resetting the Player's frame position.
 */
function useLiveComponentWrapper(
  componentRef: React.MutableRefObject<React.ComponentType<Record<string, unknown>> | null>,
) {
  // useMemo with [] creates the wrapper exactly once per component lifetime.
  // The wrapper reads componentRef.current on every render, and reads the
  // scale from the __contentScale prop (passed via inputProps) so that
  // scale changes trigger a re-render via Remotion's normal prop-diff path.
  return React.useMemo(
    () =>
      function LiveWrapper(props: Record<string, unknown>) {
        // Strip internal props — don't pass them to the user's code.
        const {
          __v: _v,
          __contentScale,
          __baseWidth,
          __baseHeight,
          __compWidth,
          __compHeight,
          ...rest
        } = props as Record<string, unknown>;
        const Inner = componentRef.current;
        if (!Inner) return null;
        const contentScale =
          typeof __contentScale === "number" && __contentScale > 0
            ? __contentScale
            : 1;

        // If the composition aspect differs from the template's native
        // aspect, render the component at native size centered inside
        // the composition with a fit-scale transform. Prevents "stuck
        // in top-left corner" when switching landscape → portrait.
        const compW = typeof __compWidth === "number" ? __compWidth : 0;
        const compH = typeof __compHeight === "number" ? __compHeight : 0;
        const baseW = typeof __baseWidth === "number" ? __baseWidth : compW;
        const baseH = typeof __baseHeight === "number" ? __baseHeight : compH;
        const compAspect = compW && compH ? compW / compH : 0;
        const baseAspect = baseW && baseH ? baseW / baseH : 0;
        const aspectDiffers =
          compAspect > 0 &&
          baseAspect > 0 &&
          Math.abs(compAspect - baseAspect) > 0.01;

        const fitScale = aspectDiffers
          ? Math.min(compW / baseW, compH / baseH)
          : 1;
        const totalScale = contentScale * fitScale;

        // Clip at composition bounds using clip-path. Unlike overflow:
        // hidden which gets confused by transforms (either clips pre-
        // transform at layout bounds, or post-transform at outer div
        // bounds — neither matches the render), clip-path clips at the
        // specified pixel region of the composition frame regardless
        // of transforms on descendants. This matches the render's
        // implicit viewport clipping exactly.
        // Use explicit pixel dimensions everywhere so preview and render
        // interpret the clipping region and inner size identically.
        // Percentages can resolve to subtly different values in the
        // browser Player vs headless Chromium.
        return (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: compW || "100%",
              height: compH || "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "relative",
                width: baseW || "100%",
                height: baseH || "100%",
                transform: totalScale === 1 ? undefined : `scale(${totalScale})`,
                transformOrigin: "center center",
              }}
            >
              <Inner {...rest} />
            </div>
          </div>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

/**
 * Error boundary that catches render-time errors from the dynamically loaded
 * component. Without this, a runtime error in the user's TSX kills the entire
 * React tree with no recovery — even fixing the code doesn't help because
 * React's error state persists until unmount.
 *
 * The boundary resets whenever `resetKey` changes (tied to moduleUrl), so
 * loading a new module clears the error and retries.
 */
class PlayerErrorBoundary extends React.Component<
  { children: React.ReactNode; resetKey: string | null; onError?: (error: Error) => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; resetKey: string | null; onError?: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error) {
    console.error("[RemotionPreview] Component render error:", error);
    this.props.onError?.(error);
  }

  override componentDidUpdate(prevProps: { resetKey: string | null }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full items-center justify-center rounded-lg bg-surface-2 p-4">
          <div className="text-center">
            <div className="mb-2 text-sm font-medium text-danger">Component render error</div>
            <pre className="max-w-md overflow-auto rounded-lg bg-code p-3 font-mono text-[10px] text-danger">
              {this.state.error?.message ?? "Unknown error"}
            </pre>
            <div className="mt-2 text-[10px] text-ink-3">
              Fix the code — the preview will retry automatically
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

interface RemotionPreviewProps {
  moduleUrl: string | null;
  inputProps: Record<string, unknown>;
  durationFrames: number;
  fps: number;
  width: number;
  height: number;
  /** Content scale (0.3-1.0) — shrinks graphic to fit overflowing content. */
  contentScale?: number;
  /** Template's native width (for aspect-match detection in the dropdown). */
  baseWidth?: number;
  /** Template's native height (for aspect-match detection in the dropdown). */
  baseHeight?: number;
  /**
   * Fires once when the internal Remotion Player ref attaches. Lets the
   * parent issue imperative commands (seek, play, pause) — used by
   * TemplateDetail's thumbnail capture to advance past the blank frame 0
   * before snapshotting. Receives null when the Player unmounts.
   */
  onPlayerRef?: (ref: PlayerRef | null) => void;
}

const ZOOM_LEVELS = [
  { label: "25%", value: 0.25 },
  { label: "50%", value: 0.5 },
  { label: "75%", value: 0.75 },
  { label: "100%", value: 1 },
  { label: "Fit", value: 0 },
];

const BG_OPTIONS = [
  { label: "Dark", value: "#0a0a0a" },
  { label: "Checker", value: "checker" },
  { label: "White", value: "#ffffff" },
  { label: "Green", value: "#00ff00" },
  { label: "Blue", value: "#0000ff" },
];

interface CanvasPreset {
  label: string;
  width: number;
  height: number;
}

export const CANVAS_PRESETS: readonly CanvasPreset[] = [
  { label: "FHD 1920×1080", width: 1920, height: 1080 },
  { label: "4K 3840×2160", width: 3840, height: 2160 },
  { label: "HD 1280×720", width: 1280, height: 720 },
  { label: "Vertical 1080×1920", width: 1080, height: 1920 },
  { label: "Square 1080×1080", width: 1080, height: 1080 },
];

export const FPS_PRESETS = [24, 30, 60] as const;

/**
 * Popover dropdown for canvas size + fps. Writes to the shared
 * `motionforge-render-settings` slot so preview and render stay in lockstep.
 * Closes on outside click and Escape (matches RenderButton.tsx pattern).
 */
export function ResolutionDropdown({
  baseWidth,
  baseHeight,
  onClose,
}: {
  baseWidth?: number;
  baseHeight?: number;
  onClose: () => void;
}) {
  const [renderSettings, setRenderSettings] = useLocalStorage<{
    width?: number;
    height?: number;
    fps?: number;
    [k: string]: unknown;
  }>("motionforge-render-settings", {});

  // Read current values directly from the render settings so the dropdown
  // always shows the user's chosen canvas size — NOT the Player's actual
  // dimensions, which lock to template native size on same-aspect picks.
  const currentWidth = renderSettings.width ?? baseWidth ?? 1920;
  const currentHeight = renderSettings.height ?? baseHeight ?? 1080;
  const currentFps = renderSettings.fps ?? 30;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  function update(patch: { width?: number; height?: number; fps?: number }) {
    setRenderSettings((prev) => ({ ...prev, ...patch }));
  }

  const isCustomCanvas = !CANVAS_PRESETS.some(
    (p) => p.width === currentWidth && p.height === currentHeight,
  );
  const isCustomFps = !FPS_PRESETS.includes(currentFps as (typeof FPS_PRESETS)[number]);

  // Aspect-match indicator: tells the user whether the chosen size keeps
  // the template's native framing or crops it.
  let aspectHint: string | null = null;
  if (baseWidth && baseHeight && baseWidth > 0 && baseHeight > 0) {
    const baseAspect = baseWidth / baseHeight;
    const targetAspect = currentWidth / currentHeight;
    const matches = Math.abs(baseAspect - targetAspect) < 0.01;
    aspectHint = matches
      ? `Same aspect as template (${baseWidth}×${baseHeight}) — content scales up`
      : `Different aspect from template (${baseWidth}×${baseHeight}) — content may crop`;
  }

  return (
    <div
      ref={containerRef}
      className="absolute right-0 top-full z-50 mt-1 flex w-[320px] flex-col gap-3 rounded-xl border border-line bg-surface-2 p-3 text-xs text-ink shadow-lg"
    >
      {/* Canvas size */}
      <section className="flex flex-col gap-2">
        <label className="text-xs font-medium text-ink-2">
          Canvas Size
        </label>
        <div className="flex flex-wrap gap-1.5">
          {CANVAS_PRESETS.map((p) => {
            const active = p.width === currentWidth && p.height === currentHeight;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => update({ width: p.width, height: p.height })}
                className={`rounded-md px-2 py-1 text-[10px] transition-colors ${
                  active
                    ? "bg-accent-soft text-accent"
                    : "bg-surface-3 text-ink-2 hover:text-ink"
                }`}
              >
                {p.label}
              </button>
            );
          })}
          <span
            className={`rounded-md px-2 py-1 text-[10px] ${
              isCustomCanvas
                ? "bg-accent-soft text-accent"
                : "bg-surface-3 text-ink-3"
            }`}
          >
            Custom
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={16}
            max={7680}
            value={currentWidth}
            onChange={(e) =>
              update({ width: parseInt(e.target.value, 10) || 0 })
            }
            className="w-24 rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-line-strong focus:outline-none"
          />
          <span className="text-ink-3">×</span>
          <input
            type="number"
            min={16}
            max={4320}
            value={currentHeight}
            onChange={(e) =>
              update({ height: parseInt(e.target.value, 10) || 0 })
            }
            className="w-24 rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-line-strong focus:outline-none"
          />
        </div>
        {aspectHint && (
          <span className="text-[10px] text-ink-3">{aspectHint}</span>
        )}
      </section>

      {/* Frame rate */}
      <section className="flex flex-col gap-2">
        <label className="text-xs font-medium text-ink-2">
          Frame Rate
        </label>
        <div className="flex items-center gap-2">
          {FPS_PRESETS.map((f) => {
            const active = currentFps === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => update({ fps: f })}
                className={`rounded-md px-3 py-1 text-xs transition-colors ${
                  active
                    ? "bg-accent-soft text-accent"
                    : "bg-surface-3 text-ink-2 hover:text-ink"
                }`}
              >
                {f}
              </button>
            );
          })}
          <input
            type="number"
            min={1}
            max={120}
            value={currentFps}
            onChange={(e) =>
              update({ fps: parseInt(e.target.value, 10) || 1 })
            }
            className={`w-20 rounded-lg border bg-surface px-2 py-1 text-xs text-ink focus:outline-none ${
              isCustomFps
                ? "border-line-strong"
                : "border-line"
            }`}
          />
          <span className="text-[10px] text-ink-3">fps</span>
        </div>
      </section>

      <span className="text-[10px] text-ink-3">
        Changes sync to render settings automatically.
        {renderSettings.width === undefined && (
          <span className="ml-1 text-ink-3">(using template defaults)</span>
        )}
      </span>
    </div>
  );
}

export function RemotionPreviewLegacy({
  moduleUrl,
  inputProps,
  durationFrames,
  fps,
  width,
  height,
  contentScale: contentScaleProp,
  baseWidth,
  baseHeight,
  onPlayerRef,
}: RemotionPreviewProps) {
  const [showResDropdown, setShowResDropdown] = useState(false);
  // Read/write the content scale from the same localStorage slot that
  // RenderButton uses, so the preview slider and render settings stay in
  // sync. The prop override wins when explicitly provided (e.g. from a
  // modal that wants to lock it).
  const [renderSettings, setRenderSettings] = useLocalStorage<{
    content_scale?: number;
    [k: string]: unknown;
  }>("motionforge-render-settings", {});
  const storedScale = renderSettings.content_scale ?? 1;
  const contentScale = contentScaleProp ?? storedScale;
  const setContentScale = useCallback(
    (v: number) => {
      // Functional update so drag-fired rapid changes don't race.
      setRenderSettings((prev) => ({ ...prev, content_scale: v }));
    },
    [setRenderSettings],
  );

  const DynamicComponent = useDynamicComponent(moduleUrl);
  const componentRef = useRef<React.ComponentType<Record<string, unknown>> | null>(null);
  componentRef.current = DynamicComponent;
  const StableComponent = useLiveComponentWrapper(componentRef);
  // Track component version so we can force the Player to re-render
  // the stable wrapper when the inner component changes, without
  // remounting the Player (which would reset frame position).
  const versionRef = useRef(0);
  const prevComponentRef = useRef(DynamicComponent);
  if (DynamicComponent !== prevComponentRef.current) {
    versionRef.current += 1;
    prevComponentRef.current = DynamicComponent;
  }
  const playerRef = useRef<PlayerRef>(null);
  // Notify the parent whenever the Player ref attaches/detaches so
  // outside code (e.g. thumbnail capture) can drive seekTo before grabbing
  // a frame. We can't pass `playerRef` directly to the parent because it
  // gets assigned by the Player component AFTER initial render.
  // Re-broadcast the Player ref each time moduleUrl changes (which is
  // when the Player remounts). We only forward non-null values — the
  // cleanup phase used to fire null which raced with the next attach,
  // leaving the parent stuck observing null even after the Player
  // mounted. The parent should treat the most recent non-null ref as
  // authoritative; component unmount of the parent handles teardown.
  useEffect(() => {
    if (playerRef.current && onPlayerRef) {
      onPlayerRef(playerRef.current);
    }
  }, [moduleUrl, onPlayerRef]);

  // Also poll briefly after each remount so the parent receives the ref
  // even if it attached just AFTER our useEffect ran. The Remotion Player
  // sometimes assigns its ref slightly out-of-band with React's commit.
  useEffect(() => {
    if (!onPlayerRef) return;
    let cancelled = false;
    const start = Date.now();
    const tick = () => {
      if (cancelled) return;
      if (playerRef.current) {
        onPlayerRef(playerRef.current);
        return;
      }
      if (Date.now() - start < 3000) {
        setTimeout(tick, 100);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [moduleUrl, onPlayerRef]);
  const stageRef = useRef<HTMLDivElement>(null);
  // Preview chrome state (zoom, background) persists in localStorage so the
  // user's view survives session switches, tab changes, and the implicit
  // remounts that happen when arriving from the Upgrade Text Fields handoff.
  // Without persistence these reset to defaults on every fresh mount, which
  // looked like a "page reload" jolt.
  const [zoom, setZoom] = useLocalStorage<number>(
    "motionforge-preview-zoom",
    0,
  ); // 0 = fit
  const [bg, setBg] = useLocalStorage<string>(
    "motionforge-preview-bg",
    "checker",
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  // Track the stage's content-box so "fit" can recompute on every layout change
  // (window resize, sidebar collapse, tab switch, fullscreen toggle, etc.).
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setStageSize({ w: cr.width, h: cr.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [DynamicComponent]);

  // Wheel-to-scroll inside the stage is modifier-gated:
  //   • Ctrl/Cmd → vertical scroll (also blocks browser's default page zoom)
  //   • Shift    → horizontal scroll (deltaY mapped to scrollLeft)
  //   • no mod   → wheel falls through and the page scrolls normally
  // Native listener with `passive: false` so preventDefault() is honored.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        el.scrollTop += e.deltaY;
        el.scrollLeft += e.deltaX;
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        // Most mice only emit deltaY; map it to horizontal scroll.
        el.scrollLeft += e.deltaY || e.deltaX;
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [DynamicComponent]);

  // Continuous frame ref — updated from every Player `frameupdate` event.
  // Used as the SOURCE for taking restore snapshots when moduleUrl changes,
  // but NOT used directly to seek (because Player's internal reset to 0
  // during component swap fires a frameupdate that overwrites this back to 0).
  const liveFrameRef = useRef<number>(0);
  // Restore target — captured ONLY at the moment moduleUrl is about to
  // change, snapping the previous live frame. The seek effect reads from
  // here so a mid-swap frameupdate(0) can't clobber our target.
  const restoreTargetRef = useRef<number>(0);

  // Sync frame position from player
  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;

    const onFrameUpdate = () => {
      const frame = p.getCurrentFrame();
      setCurrentFrame(frame);
      liveFrameRef.current = frame;
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    p.addEventListener("frameupdate", onFrameUpdate);
    p.addEventListener("play", onPlay);
    p.addEventListener("pause", onPause);

    return () => {
      p.removeEventListener("frameupdate", onFrameUpdate);
      p.removeEventListener("play", onPlay);
      p.removeEventListener("pause", onPause);
    };
  }, [DynamicComponent]);

  // Snapshot the playhead BEFORE the new module replaces the old one.
  // Triggered on moduleUrl change — at this point the Player is still
  // showing the old component, so liveFrameRef.current is the user's
  // actual position. Stash it into restoreTargetRef where it survives
  // any subsequent frameupdate(0) the Player fires during the swap.
  useEffect(() => {
    if (liveFrameRef.current > 0) {
      restoreTargetRef.current = liveFrameRef.current;
    }
  }, [moduleUrl]);

  // Restore the playhead after the new component mounts. Player's lazy
  // mount + internal reset can take >1 frame, so retry several times: 3
  // animation frames AND a 200ms timeout. Each attempt is a no-op if the
  // Player isn't ready or the seek is already at the target.
  useEffect(() => {
    if (!DynamicComponent) return;
    const target = restoreTargetRef.current;
    if (target <= 0) return;
    const p = playerRef.current;
    if (!p) return;
    let cancelled = false;
    const trySeek = () => {
      if (cancelled) return;
      try {
        const cur = p.getCurrentFrame();
        if (Math.abs(cur - target) > 1) {
          p.seekTo(Math.min(target, durationFrames - 1));
        }
      } catch { /* noop */ }
    };
    // Stagger retries to outlast the Player's internal reset window.
    const r1 = requestAnimationFrame(trySeek);
    const r2 = requestAnimationFrame(() => requestAnimationFrame(trySeek));
    const t = setTimeout(trySeek, 200);
    return () => {
      cancelled = true;
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      clearTimeout(t);
    };
  }, [DynamicComponent, durationFrames]);

  if (!DynamicComponent) {
    return (
      <div className="flex h-64 w-full items-center justify-center rounded-lg border border-line bg-surface-2">
        <div className="flex items-center gap-3">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-accent" />
          <span className="text-sm text-ink-2">
            Loading preview...
          </span>
        </div>
      </div>
    );
  }

  const checkerBg = bg === "checker"
    ? {
        backgroundImage:
          "linear-gradient(45deg, #1a1a1a 25%, transparent 25%), " +
          "linear-gradient(-45deg, #1a1a1a 25%, transparent 25%), " +
          "linear-gradient(45deg, transparent 75%, #1a1a1a 75%), " +
          "linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)",
        backgroundSize: "20px 20px",
        backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
        backgroundColor: "#0f0f0f",
      }
    : { backgroundColor: bg };

  return (
    <div className="flex w-full flex-col gap-2">
      {/* Controls bar */}
      <div className="flex items-center justify-between rounded-lg border border-line bg-surface-2 px-3 py-1.5">
        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <span className="mr-2 text-xs text-ink-3">Zoom:</span>
          {ZOOM_LEVELS.map((z) => (
            <button
              key={z.label}
              onClick={() => setZoom(z.value)}
              className={`rounded-md px-2 py-0.5 text-xs transition-colors ${
                zoom === z.value
                  ? "bg-accent-soft text-accent"
                  : "text-ink-3 hover:text-ink"
              }`}
            >
              {z.label}
            </button>
          ))}
        </div>

        {/* Background controls */}
        <div className="flex items-center gap-1">
          <span className="mr-2 text-xs text-ink-3">BG:</span>
          {BG_OPTIONS.map((b) => (
            <button
              key={b.label}
              onClick={() => setBg(b.value)}
              className={`rounded-md px-2 py-0.5 text-xs transition-colors ${
                bg === b.value
                  ? "bg-accent-soft text-accent"
                  : "text-ink-3 hover:text-ink"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>

        {/* Content scale (links to render settings) */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-3">Scale:</span>
          <input
            type="range"
            min={0.3}
            max={1.0}
            step={0.05}
            value={contentScale}
            onChange={(e) => setContentScale(parseFloat(e.target.value))}
            className="w-28 accent-accent"
            title="Content scale — shrinks the graphic to fit overflowing text. Linked to render settings."
          />
          <span className="w-9 text-xs text-ink-2">
            {Math.round(contentScale * 100)}%
          </span>
        </div>

        {/* Resolution / fps dropdown — writes to motionforge-render-settings
            so preview and render share the same source of truth. */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowResDropdown((v) => !v)}
            className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2 py-0.5 text-xs text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
            title="Change canvas size and frame rate"
            aria-label="Canvas size and frame rate"
            aria-expanded={showResDropdown}
          >
            <span>
              {(renderSettings.width as number | undefined) ?? width}×
              {(renderSettings.height as number | undefined) ?? height} @{" "}
              {(renderSettings.fps as number | undefined) ?? fps}fps
            </span>
            <span className="text-[10px]">▾</span>
          </button>
          {showResDropdown && (
            <ResolutionDropdown
              baseWidth={baseWidth}
              baseHeight={baseHeight}
              onClose={() => setShowResDropdown(false)}
            />
          )}
        </div>
      </div>

      {/* Player stage. The stage has a fixed height so its content box is
          measurable; the inner wrapper is sized to the chosen scale and
          centered. Fit = min(stageW/compW, stageH/compH) so the entire frame
          is always visible regardless of which axis is the constraint. */}
      <div
        ref={stageRef}
        className="flex items-center justify-center overflow-hidden rounded-lg rounded-b-none border border-b-0 border-line"
        style={{
          ...checkerBg,
          height: "65vh",
        }}
      >
        {(() => {
          const fitScale =
            stageSize.w > 0 && stageSize.h > 0
              ? Math.min(stageSize.w / width, stageSize.h / height)
              : 0;
          const scale = zoom === 0 ? fitScale : zoom;
          const w = scale > 0 ? Math.floor(width * scale) : width;
          const h = scale > 0 ? Math.floor(height * scale) : height;
          // Explicit fit-scale applied via an OUTER CSS transform. This
          // makes the Player's internal canvas exactly compW×compH so
          // its own scale is 1:1, eliminating Player-internal subpixel
          // transforms from the clipping math. Without this, Remotion
          // Player's auto-fit applies `floor(width*s)/width` which can
          // land on a slightly different fractional scale than we think
          // we asked for, and causes the preview to shift content by
          // 1–2 pixels relative to what the server-side render produces
          // at the same content_scale.
          const explicitScale = scale > 0 ? scale : 1;
          return (
            <div
              style={{
                width: w,
                height: h,
                flexShrink: 0,
                position: "relative",
                // Clip at display bounds — matches the render's implicit
                // viewport clip (compW×compH scaled down to display).
                overflow: "hidden",
                outline: "1px solid rgba(0, 255, 65, 0.35)",
                boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.55)",
              }}
            >
              {/* Scale-wrap: full compW×compH in its own coord system,
                  CSS-scaled down to fit the display box. The Player
                  mounts inside at exact compW×compH so its canvas-to-
                  composition scale is 1:1 — then OUR transform shrinks
                  everything uniformly. This is the mirror image of the
                  render, which rasterizes at compW×compH then Remotion
                  downsamples for output. */}
              <div
                style={{
                  width: width,
                  height: height,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transform: `scale(${explicitScale})`,
                  transformOrigin: "top left",
                  overflow: "hidden",
                }}
              >
                <PlayerErrorBoundary resetKey={moduleUrl}>
                  <Player
                    ref={playerRef}
                    component={StableComponent}
                    style={{ width, height, overflow: "hidden" }}
                    inputProps={{
                      ...inputProps,
                      __v: versionRef.current,
                      __contentScale: contentScale,
                      __baseWidth: baseWidth,
                      __baseHeight: baseHeight,
                      __compWidth: width,
                      __compHeight: height,
                    }}
                    durationInFrames={durationFrames}
                    fps={fps}
                    compositionWidth={width}
                    compositionHeight={height}
                    loop
                  />
                </PlayerErrorBoundary>
              </div>
            </div>
          );
        })()}
      </div>

      {/* External playback controls — no overlap with preview */}
      <div className="flex items-center gap-3 rounded-lg rounded-t-none border border-line bg-surface-2 px-3 py-2">
        <button
          onClick={() => {
            const p = playerRef.current;
            if (!p) return;
            if (isPlaying) { p.pause(); } else { p.play(); }
            setIsPlaying(!isPlaying);
          }}
          className="flex h-7 w-7 items-center justify-center rounded-md border border-line bg-surface text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          {isPlaying ? (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <rect x="1" y="1" width="4" height="10" rx="1" />
              <rect x="7" y="1" width="4" height="10" rx="1" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <polygon points="2,0 12,6 2,12" />
            </svg>
          )}
        </button>

        {/* Seek bar */}
        <input
          type="range"
          min={0}
          max={durationFrames - 1}
          value={currentFrame}
          onChange={(e) => {
            const frame = Number(e.target.value);
            playerRef.current?.seekTo(frame);
            setCurrentFrame(frame);
          }}
          className="flex-1 accent-accent"
        />

        {/* Frame counter */}
        <span className="min-w-[80px] text-right text-xs tabular-nums text-ink-2">
          {currentFrame} / {durationFrames}
        </span>
      </div>
    </div>
  );
}
